import { useGetApiWorkspacesByIdEntitlementsQuery } from '@/model/entitlements/api.generated';
import type { Entitlement, EntitlementsResponse } from '@/model/entitlements/dto.generated';
import { useAppSelector } from '@/shared/hooks/useStore';
import { selectUserData } from '@/viewmodel/features/user';

export type LimitKey = Entitlement['key'];

interface WorkspaceScope {
  readonly workspaces?: { readonly currentWorkspace?: { readonly id: string } | undefined };
}

const selectCurrentWorkspace = (state: WorkspaceScope): { readonly id: string } | null =>
  state.workspaces?.currentWorkspace ?? null;
import { unwrapEnvelope } from '@/model/client';
import { isInternalTeramotEmail } from '@/model/user/user.model';
import { toTrialView } from '@/viewmodel/features/entitlements/mappers/trialView';
import type { TrialView } from '@/viewmodel/features/entitlements/types';

const STAFF_EXEMPT: ReadonlySet<LimitKey> = new Set<LimitKey>([
  'gold_tables',
  'source_tables',
  'dashboards',
  'incremental_refresh',
  'custom_refresh_cadence',
  'studio_documents',
]);

export const useEntitlementsQuery = (
  workspaceIdOverride?: string,
): {
  entitlements: EntitlementsResponse | null;
  get: (key: LimitKey) => Entitlement | undefined;
  hasRoom: (key: LimitKey, usedOverride?: number) => boolean;
  isExempt: (key: LimitKey) => boolean;
  isLoading: boolean;
  trial: TrialView | null;
  workspaceId: string;
} => {
  const workspace = useAppSelector(selectCurrentWorkspace);
  const { data: user } = useAppSelector(selectUserData);
  const isStaff = isInternalTeramotEmail(user?.email);
  const workspaceId = workspaceIdOverride ?? workspace?.id ?? '';
  const { data, isLoading } = useGetApiWorkspacesByIdEntitlementsQuery(
    { id: workspaceId },
    { skip: !workspaceId },
  );
  const entitlements = data ? unwrapEnvelope<EntitlementsResponse>(data) : null;
  const get = (key: LimitKey) => entitlements?.items.find((item) => item.key === key);
  const isExempt = (key: LimitKey) => isStaff && STAFF_EXEMPT.has(key);
  const hasRoom = (key: LimitKey, usedOverride?: number) => {
    if (isExempt(key)) return true;
    const item = get(key);
    if (item?.limit === null || item?.limit === undefined) return true;
    const used = usedOverride ?? item.used ?? 0;
    return used < item.limit;
  };

  const trial = toTrialView(entitlements?.trial);

  return {
    entitlements,
    get,
    hasRoom,
    isExempt,
    isLoading,
    trial: isStaff && trial && !trial.started ? null : trial,
    workspaceId,
  };
};
